/**
 * ConnectorPickerButton — the composer's "Sources" picker: what Bower is
 * allowed to READ this chat.
 *
 * Two sections in one popover (mirrors Claude's "+" menu shape):
 *   1. **Web search** — top-level mode toggle, on by default. Bower
 *      uses Google Search via the `web_search` tool unless the user
 *      flips this off for the turn.
 *   2. **Connectors** — one toggle per ACTIVE managed-MCP-gateway
 *      connection (Benchling-style "+ → MCPs" submenu).
 *
 * State lives in `chatConnectorsStore` — web-search preference is
 * global, per-toolkit toggles are per-workspace. Both persisted to
 * localStorage so reloads don't surprise the user.
 *
 * Empty connector state surfaces a one-line CTA + deep-link to the
 * Connectors settings tab — onboarding affordance, not a dead UI.
 *
 * The agent-service consumes the picker state as multipart fields
 * (`web_search_enabled`, `enabled_connectors`) on `/api/chat`; see
 * services/agentChat.service.ts. Web search is honoured by the
 * web_search tool's short-circuit guard. ``enabled_connectors`` is
 * read by ``ComposioToolset.get_tools()`` per turn to decide which
 * toolkits Bower can call (see docs/architecture/connectors.md).
 */

import { Badge, Box, Button, HStack, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { ConnectionSummary, ToolkitCatalogEntry } from '@bower/shared';
import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { ROUTES } from '../../../constants/routes';
import { useWorkspace, useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useConnections, useToolkitCatalog } from '../../../hooks/data/useConnectors';
import { useIsPhiWorkspace } from '../../../hooks/user/useIsPhiWorkspace';
import { useChatConnectorsStore } from '../../../stores/chatConnectorsStore';
import { safeAnalytics } from '../../../utils/analytics';
import { ACTION_ROW_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { COMPOSER_LABEL_CLASS } from './composerMetrics';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { ResponsivePopover } from '../../ui/ResponsivePopover';
import { ConnectorList } from './ConnectorList';
import { EmptyConnectorsPrompt } from './EmptyConnectorsPrompt';

interface ConnectorPickerButtonProps {
  readonly isDisabled?: boolean;
  /** Accessible name for the trigger, without the enabled-count suffix. */
  readonly sourcesLabel?: string;
}

const DEFAULT_SOURCES_LABEL = 'Sources Bower can read in this chat';

interface ToggleProps {
  readonly checked: boolean;
  readonly onClick: () => void;
  readonly ariaLabel: string;
}

/**
 * One first-party source row: Bower's own mark, a name, a sub-line, and
 * whatever control (or lack of one) decides whether it is on.
 *
 * Shared by the two first-party rows because they are the same row with a
 * different trailing control. Connector rows deliberately do NOT use it —
 * their leading visual is the third party's own logo, and a brand mark is not
 * ours to paint.
 */
function SourceRow({
  icon,
  title,
  subtitle,
  trailing,
}: {
  readonly icon: string;
  readonly title: string;
  readonly subtitle: string;
  readonly trailing: ReactNode;
}) {
  return (
    <HStack spacing={3} py={2} px={1} borderRadius="md" _hover={{ bg: 'bg.hover' }}>
      {/* Gradient: these are Bower's own source-type marks, and each sits
          beside its name, so the glyph is never the only thing identifying the
          row. The connector rows below stay flat for the opposite reason. */}
      <MaterialIcon name={icon} boxSize={5} gradient="warm" />
      <Box flex={1} minW={0}>
        <Text fontSize="sm" color="text.primary" noOfLines={1}>
          {title}
        </Text>
        <Text fontSize="2xs" color="text.muted" noOfLines={1}>
          {subtitle}
        </Text>
      </Box>
      {trailing}
    </HStack>
  );
}

/**
 * The workspace row. Always on, and that is the POINT of showing it.
 *
 * Bower could always read the workspace; nothing in this popover said so, so
 * "Sources" read as a list of optional extras and left the most important
 * source invisible. The 2026-09 design puts it first, above the things you can
 * actually switch.
 *
 * It renders static text, NOT a disabled `role="switch"`. A switch says "this
 * is yours to change" and a disabled one says "…but not right now", which is a
 * different and wrong claim — this is never togglable. Assistive tech gets the
 * same sentence a sighted user reads.
 */
function WorkspaceSourceRow() {
  return (
    <SourceRow
      icon="folder"
      title="Your workspace"
      subtitle="Notes, protocols, and equipment"
      trailing={
        <Text fontSize="2xs" color="text.muted" flexShrink={0} px={2} whiteSpace="nowrap">
          Always on
        </Text>
      }
    />
  );
}

interface WebSearchRowProps {
  readonly enabled: boolean;
  readonly onToggle: () => void;
}

function WebSearchRow({ enabled, onToggle }: WebSearchRowProps) {
  return (
    <SourceRow
      icon="public"
      title="Public sources"
      subtitle="Web and the open scientific record"
      trailing={
        <Toggle
          checked={enabled}
          onClick={onToggle}
          ariaLabel="Toggle public-source lookups in this chat"
        />
      }
    />
  );
}

function ConnectorsSectionHeader() {
  return (
    <Text
      fontSize="2xs"
      fontWeight="semibold"
      textTransform="uppercase"
      letterSpacing="wider"
      color="text.muted"
      px={1}
      pt={1}
      pb={1}
    >
      Connectors
    </Text>
  );
}

/**
 * Pure-CSS toggle styled as a switch.
 *
 * Chakra's `<Switch>` pulls in `@zag-js/focus-visible`, which tries to
 * patch `HTMLElement.prototype.focus` at mount — incompatible with
 * jsdom 24 in tests. A button-based toggle gives us the same UX with
 * none of the dependency surface.
 */
function Toggle({ checked, onClick, ariaLabel }: ToggleProps) {
  // Visual switch is intentionally compact (32×18) but the touch
  // target is enlarged to 44×44 via padding so it meets WCAG 2.5.5.
  // The visible track + thumb are unchanged — the click area is just
  // bigger than the pixels the eye sees.
  return (
    <Box
      as="button"
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onClick}
      minW="44px"
      minH="44px"
      // Padding makes the inner pseudo-element render at the original
      // visual size (32×18) inside a 44×44 hit area, centred.
      px="6px"
      py="13px"
      borderRadius="full"
      bg="transparent"
      position="relative"
      flexShrink={0}
      _focusVisible={{ boxShadow: 'outline' }}
      sx={{
        // Visible track painted via ::before so the outer Box's tap
        // surface stays larger than the visible switch.
        '&::before': {
          content: '""',
          display: 'block',
          width: '32px',
          height: '18px',
          borderRadius: '9999px',
          background: checked
            ? 'var(--chakra-colors-status-success)'
            : 'var(--chakra-colors-bg-muted)',
          transition: 'background 0.15s ease',
        },
      }}
    >
      <Box
        position="absolute"
        // Thumb floats over the ::before track. Track sits at
        // (px=6, py=13) inside the 44×44 Box; thumb is 14×14 so
        // (track-y 13 + (track-h 18 − 14)/2 = 15px) vertically and
        // (track-x 6 + 2/16 of track depending on state) horizontally.
        top="15px"
        left={checked ? '22px' : '8px'}
        w="14px"
        h="14px"
        borderRadius="full"
        bg="bg.canvas"
        boxShadow="sm"
        transition="left 0.15s ease"
        // iOS Safari doesn't reliably bubble taps from a non-clickable
        // child to a parent button; users tap the visually obvious thumb
        // and nothing happens. Take the thumb out of hit-testing so the
        // whole 44×44 surface is the button. (Desktop mouse clicks bubble
        // either way, which is why the bug was mobile-only.)
        pointerEvents="none"
      />
    </Box>
  );
}

function ConnectorPickerButton({
  isDisabled,
  sourcesLabel = DEFAULT_SOURCES_LABEL,
}: ConnectorPickerButtonProps) {
  // PHI workspaces lose the picker entirely — same posture as the
  // Connectors tab in Settings — because the gateway provider's BAA
  // isn't signed yet. Server-side enforcement still blocks every
  // connector tool when ``isPhiWorkspace`` flips on, but hiding the
  // UI keeps the user from opening a panel that has nothing to do.
  // Hidden too while the workspace is still loading: `useIsPhiWorkspace`
  // reads false then, and the dashboard composer mounts before the
  // workspace's details arrive, so a PHI workspace would flash the picker.
  const { workspace } = useWorkspace();
  const isPhiWorkspace = useIsPhiWorkspace();
  const connectorsEnabled = workspace !== null && !isPhiWorkspace;
  const workspaceId = useWorkspaceId();
  const connectionsQuery = useConnections();
  const catalogQuery = useToolkitCatalog();
  const navigate = useNavigate();

  // Subscribe to the DATA (`disabledByWorkspace`), not the store's `isEnabled`
  // method. Selecting the method returns a stable ref that never changes, so a
  // per-toolkit toggle mutated the store but never re-rendered this component —
  // the switch looked frozen even though the click worked. `webSearchEnabled`
  // worked because it subscribes to a value. Derive enablement locally from the
  // subscribed map so a toggle re-renders. (Pinned by ConnectorPickerButton.test.)
  const disabledByWorkspace = useChatConnectorsStore((s) => s.disabledByWorkspace);
  const toggle = useChatConnectorsStore((s) => s.toggle);
  const webSearchEnabled = useChatConnectorsStore((s) => s.webSearchEnabled);
  const toggleWebSearch = useChatConnectorsStore((s) => s.toggleWebSearch);

  const isEnabled = (ws: string, toolkitKey: string) =>
    !(disabledByWorkspace[ws] ?? []).includes(toolkitKey);

  const activeConnections = useMemo<ConnectionSummary[]>(
    () => (connectionsQuery.data ?? []).filter((c) => c.status === 'ACTIVE'),
    [connectionsQuery.data],
  );

  const catalogByKey = useMemo<Map<string, ToolkitCatalogEntry>>(() => {
    const map = new Map<string, ToolkitCatalogEntry>();
    (catalogQuery.data ?? []).forEach((t) => map.set(t.key, t));
    return map;
  }, [catalogQuery.data]);

  const enabledConnectorCount = workspaceId
    ? activeConnections.filter((c) => isEnabled(workspaceId, c.toolkitKey)).length
    : 0;
  // Badge count includes web search so a quick glance at the trigger
  // tells the user how many tools Bower has live this turn.
  const enabledCount = enabledConnectorCount + (webSearchEnabled ? 1 : 0);

  const handleToggle = (toolkitKey: string) => {
    if (!workspaceId) return;
    const wasEnabled = isEnabled(workspaceId, toolkitKey);
    toggle(workspaceId, toolkitKey);
    safeAnalytics.track(ANALYTICS_EVENTS.CONNECTOR_TOGGLED_IN_CHAT, {
      toolkit_key: toolkitKey,
      enabled: !wasEnabled,
    });
  };

  const handleToggleWebSearch = () => {
    const wasEnabled = webSearchEnabled;
    toggleWebSearch();
    safeAnalytics.track(ANALYTICS_EVENTS.PUBLIC_SOURCES_TOGGLED_IN_CHAT, {
      enabled: !wasEnabled,
    });
  };

  const handleOpenSettings = () => {
    navigate(`${ROUTES.settings}?tab=connectors`);
  };

  if (!connectorsEnabled) {
    return null;
  }

  return (
    <ResponsivePopover
      tooltip="What Bower can read in this chat"
      placement="top-start"
      width="320px"
      title={
        <>
          <MaterialIcon name="manage_search" boxSize={3.5} color="brand.onSurface" />
          Sources
        </>
      }
      trigger={
        <Button
          // "Sources", not "Tools" — this panel is about what Bower can READ;
          // "tools" reads as internal plumbing. The count badge is the
          // affordance that answers "what can Bower reach right now" without
          // opening anything.
          // The count lives inside the button, so aria-label would otherwise
          // swallow it — an aria-label wins the accessible-name computation and
          // the Badge would never be announced.
          aria-label={enabledCount > 0 ? `${sourcesLabel}, ${enabledCount} enabled` : sourcesLabel}
          // The shared composer glyph size — see `ui/iconButtonMetrics.ts`. All four
          // controls in this row take it, so they cannot drift apart again, and
          // it matters most once `.composer-label` collapses the word "Sources"
          // and the glyph is left carrying the control on its own.
          leftIcon={<MaterialIcon name="manage_search" boxSize={ACTION_ROW_GLYPH_SIZE} />}
          size="sm"
          variant="ghost"
          color="text.secondary"
          fontWeight="medium"
          borderRadius="lg"
          isDisabled={isDisabled}
          h="44px"
          px={2}
          flexShrink={0}
        >
          <HStack spacing={1.5}>
            <Text className={COMPOSER_LABEL_CLASS} fontSize="sm" color="inherit">
              Sources
            </Text>
            {enabledCount > 0 && (
              <Badge
                variant="success"
                size="sm"
                // A COUNT, so it wants to be a circle rather than a lozenge:
                // minW + centring give it one at a single digit and let it
                // stretch to a pill at two. `justifyContent`, not
                // `textAlign` — the Badge base style is inline-flex.
                minW="18px"
                justifyContent="center"
                px={1}
              >
                {enabledCount}
              </Badge>
            )}
          </HStack>
        </Button>
      }
    >
      <Text fontSize="xs" color="text.muted" mb={2}>
        Choose what Bower can use in this chat.
      </Text>
      {/* Workspace first, then the things that can be switched. The count
          badge on the trigger deliberately does NOT include this row: it
          answers "how many did I turn on", and a source that is always on was
          never turned on. */}
      <WorkspaceSourceRow />
      <WebSearchRow enabled={webSearchEnabled} onToggle={handleToggleWebSearch} />
      <Box my={1} borderBottomWidth="1px" borderColor="border.muted" />
      <ConnectorsSectionHeader />
      {activeConnections.length === 0 ? (
        <EmptyConnectorsPrompt onOpenSettings={handleOpenSettings} />
      ) : (
        <ConnectorList
          connections={activeConnections}
          catalogByKey={catalogByKey}
          workspaceId={workspaceId}
          isEnabled={isEnabled}
          onToggle={handleToggle}
          onOpenSettings={handleOpenSettings}
          Toggle={Toggle}
        />
      )}
    </ResponsivePopover>
  );
}

export { ConnectorPickerButton };
